import { ArrowDown, ArrowUp, ListChecks, X } from "lucide-react";
import type { FC } from "react";

import {
  Queue,
  QueueItem,
  QueueItemAction,
  QueueItemActions,
  QueueItemContent,
  QueueItemIndicator,
  QueueList,
  QueueSection,
  QueueSectionContent,
  QueueSectionLabel,
  QueueSectionTrigger
} from "@/components/ai-elements/queue";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import type { PlanTodo } from "../types";
import { todoProgress, todoToQueueItem } from "./model";

type Props = {
  todos: readonly PlanTodo[];
  label?: string;
  defaultOpen?: boolean;
  className?: string;
  /** Let the list grow instead of scrolling inside a fixed height. */
  unbounded?: boolean;
  /** Edit affordances (plan pane in edit mode). */
  onRemove?: (id: string) => void;
  onMove?: (id: string, direction: -1 | 1) => void;
};

/**
 * A plan's todos as an AI Elements `Queue`. `in_progress` shows a spinner in
 * place of the indicator; `cancelled` is struck through without the done mark.
 */
export const TodoQueue: FC<Props> = ({
  todos,
  label = "Todos",
  defaultOpen = true,
  className,
  unbounded,
  onRemove,
  onMove
}) => {
  const progress = todoProgress(todos);
  const items = todos.map(todoToQueueItem);
  return (
    <Queue className={cn("text-xs", className)} data-testid="todo-queue">
      <QueueSection defaultOpen={defaultOpen}>
        <QueueSectionTrigger className="px-2 py-1.5 text-xs">
          <QueueSectionLabel
            label={`${label} · ${progress.label}`}
            icon={<ListChecks className="size-3.5" />}
          />
        </QueueSectionTrigger>
        <QueueSectionContent>
          <QueueList className={cn(unbounded && "[&>div]:max-h-none")}>
            {items.map((item, index) => (
              <QueueItem
                key={item.id}
                data-testid={`todo-${item.id}`}
                data-status={item.status}
                className="group py-1"
              >
                {item.active ? (
                  <Spinner
                    className="mt-0.5 size-3 shrink-0 text-primary"
                    aria-label="In progress"
                  />
                ) : (
                  <QueueItemIndicator
                    completed={item.completed && !item.cancelled}
                    className={cn(item.cancelled && "border-dashed")}
                  />
                )}
                <QueueItemContent
                  completed={item.completed}
                  className={cn(
                    "line-clamp-none",
                    item.active && "text-foreground",
                    item.cancelled && "italic"
                  )}
                >
                  {item.content}
                </QueueItemContent>
                {onRemove || onMove ? (
                  <QueueItemActions>
                    {onMove ? (
                      <>
                        <QueueItemAction
                          aria-label="Move up"
                          disabled={index === 0}
                          onClick={() => onMove(item.id, -1)}
                        >
                          <ArrowUp className="size-3" />
                        </QueueItemAction>
                        <QueueItemAction
                          aria-label="Move down"
                          disabled={index === items.length - 1}
                          onClick={() => onMove(item.id, 1)}
                        >
                          <ArrowDown className="size-3" />
                        </QueueItemAction>
                      </>
                    ) : null}
                    {onRemove ? (
                      <QueueItemAction
                        aria-label="Remove todo"
                        onClick={() => onRemove(item.id)}
                      >
                        <X className="size-3" />
                      </QueueItemAction>
                    ) : null}
                  </QueueItemActions>
                ) : null}
              </QueueItem>
            ))}
          </QueueList>
        </QueueSectionContent>
      </QueueSection>
    </Queue>
  );
};
